import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { Play, Calendar, Sparkles, Youtube, ExternalLink } from 'lucide-react';
import { fetchVideos } from '../services/api';
import { Fireflies, Moth } from '../components/FairyElements';

const Videos = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [filter, setFilter] = useState('all');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideos().then(setVideos).catch(() => setVideos([])).finally(() => setLoading(false));
  }, []);

  const filteredVideos = filter === 'all' ? videos : videos.filter(v => v.type === filter);
  const categories = ['all', 'video', 'short'];

  return (
    <div className="min-h-screen bg-[#0a1612]">
      <section className="forest-bg min-h-[500px] flex items-center px-4 md:px-8 lg:px-16 py-20" style={{ '--forest-image': "url(/mystical-garden/videos-stage.png)", '--bg-opacity': 0.6}}>
        <Fireflies count={40} />
        <Moth delay={0} top="30%" />

        <div className="max-w-6xl mx-auto w-full text-center relative z-10 animate-fadeIn">
          <Badge className="mb-4 bg-emerald-900/60 border border-amber-500/40 text-amber-300 backdrop-blur-md">
            <Sparkles className="w-3 h-3 mr-1.5" />
            <span className="tracking-widest text-xs uppercase">Sacred Teachings</span>
          </Badge>
          <h1 className="text-6xl md:text-7xl font-bold mb-6 gradient-text text-glow">Video Guidance</h1>
          <p className="text-lg md:text-xl text-amber-100/80 max-w-3xl mx-auto mb-10 font-serif-mystical italic">
            Watch spiritual guidance videos, tarot readings, and Navratri files from The Divine7777 YouTube channel.
          </p>

          <Button
            size="lg"
            className="btn-gold px-8 py-6"
            onClick={() => window.open('https://www.youtube.com/@thedivine7777', '_blank')}
            data-testid="youtube-channel-btn"
          >
            <Youtube className="mr-2 h-5 w-5" />
            Visit YouTube Channel
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <section className="py-10 px-4 md:px-8 lg:px-16 section-deep">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center gap-3 flex-wrap">
            {categories.map((cat) => (
              <Button
                key={cat}
                className={filter === cat ? 'btn-gold' : 'btn-mystical'}
                onClick={() => setFilter(cat)}
                data-testid={`filter-${cat}-btn`}
              >
                {cat === 'all' ? 'All Videos' : cat === 'video' ? 'Long Videos' : 'Shorts'}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <section className="section-deep pb-24 px-4 md:px-8 lg:px-16 relative">
        <Fireflies count={25} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              <p className="text-amber-100/60 col-span-full text-center py-12">Loading videos...</p>
            ) : filteredVideos.length === 0 ? (
              <p className="text-amber-100/60 col-span-full text-center py-12">No videos in this category yet.</p>
            ) : filteredVideos.map((video) => (
              <Card
                key={video.id}
                className="forest-card border-0 overflow-hidden group cursor-pointer"
                onClick={() => setSelectedVideo(video)}
                data-testid={`video-card-${video.id}`}
              >
                <div className="h-56 overflow-hidden bg-emerald-900/40 relative">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mystical-glow-gold">
                      <Play className="w-8 h-8 text-emerald-950 ml-1" fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3 bg-black/75 text-amber-300 text-xs px-2 py-1 rounded">
                    {video.duration}
                  </div>
                  {video.type === 'short' && (
                    <div className="absolute top-3 left-3 bg-amber-600 text-emerald-950 text-xs px-2 py-1 rounded font-semibold">
                      SHORT
                    </div>
                  )}
                </div>

                <CardHeader>
                  <Badge className="mb-3 w-fit bg-emerald-800/80 text-amber-300 border border-amber-500/30">{video.category}</Badge>
                  <CardTitle className="text-xl text-amber-200 group-hover:text-amber-300 transition-colors line-clamp-2" style={{ fontFamily: 'Cinzel, serif' }}>{video.title}</CardTitle>
                  <CardDescription className="text-amber-100/70 line-clamp-2">{video.description}</CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="flex items-center text-sm text-amber-100/60">
                    <Calendar className="w-4 h-4 mr-2 text-amber-500" />
                    {new Date(video.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-amber-500/40">
          <DialogTitle className="sr-only">{selectedVideo?.title}</DialogTitle>
          {selectedVideo && (
            <div className="aspect-video w-full">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?autoplay=1`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <section className="forest-bg py-24 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/videos-stage.png)", '--bg-opacity': 0.5}}>
        <Fireflies count={40} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">Subscribe for More</h2>
          <p className="text-lg mb-8 text-amber-100/80 font-serif-mystical italic">
            Follow The Divine7777 on YouTube for weekly readings, Navratri files, and spiritual insights.
          </p>
          <Button size="lg" className="btn-gold px-10 py-7" onClick={() => window.open('https://www.youtube.com/@thedivine7777', '_blank')}>
            <Youtube className="mr-2 h-5 w-5" />
            Subscribe on YouTube
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Videos;
